/* eslint-disable import/prefer-default-export */

import { SlugError } from '../console';

import type { Timer } from './_types';

interface CallPromseOptions<T, Context> {
  dataSource: (ctx: Context) => Promise<T>;
  defaultValue?: T;

  timeout?: number;
  timeoutError?: SlugError;

  context?: Context;
  setup?: (ctx: Context) => void;
  cleanup?: (ctx: Context) => void;
}

export const callPromise = <T = any, Context = any>({
  dataSource,
  defaultValue,
  timeout = 1000,
  context,
  setup,
  cleanup,
  timeoutError,
}: CallPromseOptions<T, Context>) => new Promise<T>((resolve, reject) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const ctx: Context = context ?? ({} as Context);
  setup?.(ctx);
  let timer: Timer;
  if (timeout >= 0) {
    timer = setTimeout(() => {
      cleanup?.(ctx);
      defaultValue
        ? resolve(defaultValue)
        : reject(timeoutError ?? new SlugError('timeout', 'callPromise'));
    }, timeout);
  }
  dataSource(ctx).then(resolve)
    .catch(reject)
    .finally(() => {
      clearTimeout(timer);
      cleanup?.(ctx);
    });
  // dataSource(ctx)
  //   .then((d) => {
  //     cleanup?.(ctx);
  //     resolve(d);
  //   })
  //   .catch((e) => {
  //     cleanup?.(ctx);
  //     reject(e);
  //   })
  //   .finally(() => {
  //     clearTimeout(timer);
  //   });
});

export type ErrorFirstCallback<T> = (err: any, arg?: T) => void;
export interface CustomPromisifyLegacy<TCustom extends Function> extends Function {
    __promisify__: TCustom;
}

export function promisify<TCustom extends Function>(fn: CustomPromisifyLegacy<TCustom>): TCustom;
// TODO: 处理 typescript 最后一个参数确定的更好办法（该方案来自 node/ts3.1/util.d.ts）
export function promisify<T>(
  fn: (cb: ErrorFirstCallback<T>) => void
): () => Promise<T>;
export function promisify<T>(
  fn: (cb: ErrorFirstCallback<T>) => void
): () => Promise<T>;
export function promisify<T1, T>(
  fn: (a1: T1, cb: ErrorFirstCallback<T>) => void
): (a1: T1) => Promise<T>;
export function promisify<T1, T2, T>(
  fn: (a1: T1, a2: T2, cb: ErrorFirstCallback<T>) => void
): (a1: T1, a2: T2) => Promise<T>;
export function promisify<T1, T2, T3, T>(
  fn: (a1: T1, a2: T2, a3: T3, cb: ErrorFirstCallback<T>) => void
): (a1: T1, a2: T2, a3: T3) => Promise<T>;
export function promisify<T1, T2, T3, T4, T>(
  fn: (a1: T1, a2: T2, a3: T3, a4: T4, cb: ErrorFirstCallback<T>) => void
): (a1: T1, a2: T2, a3: T3, a4: T4) => Promise<T>;
export function promisify<T1, T2, T3, T4, T5, T>(
  fn: (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5, cb: ErrorFirstCallback<T>) => void
): (a1: T1, a2: T2, a3: T3, a4: T4, a5: T5) => Promise<T>;
export function promisify(fn: any) {
  return (...args: any) => new Promise((resolve, reject) => {
    // eslint-disable-next-line no-underscore-dangle
    fn.__promisify__
      // eslint-disable-next-line no-underscore-dangle
      ? fn.__promisify__(...args)
        .then(resolve)
        .catch(reject)
      : fn(...args, (err: any, arg: any) => (err ? reject(err) : resolve(arg)));
  });
};

export const debugPromise = <T = any>(p: Promise<T>, debug?: Function) => {
  if (!debug) {
    return p;
  }
  const { then } = p;
  // eslint-disable-next-line
  p.then = (...[onfulfilled, onrejected]: Parameters<typeof then>): any => {
    return debugPromise(then.call(p, ((v: T) => {
      debug(v);
      return !!onfulfilled ? onfulfilled(v) : v;
    }) as any, onrejected as any), debug);
  };
  return p;
};
